import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import getConfig from "../config";
import toast from "../hooks/toast";
import { buildApiV3Url, previousDays } from "../shared/Utils";
import { CRUDOperation, rowsPerPageDefault } from "../shared/constants";
import { parseGetAllObjResponse, parseSuccessResponse } from "../shared/responseUtils";

const { hostnames } = getConfig();
const { docLockerClient } = hostnames;
const [showToast, hideToast] = toast();

const initialState = {
    isLoading: false,
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 0,
            totalRecords: 0,
        },
    },
    formState: {
        toDate: new Date().toISOString().slice(0, 10),
        fromDate: previousDays(30),
        validator: "",
        viewDetail: false,
    },
    validatorsList: null,
    isLoadingValidatorsList: false,
    sortColumn: ["complete_date", false], // false indicates sort by desc
    isExportValidationReportLoading: false,
    isDialogOpenValidationReportExport: false,
};

export const validationReportSlice = createSlice({
    name: "validationReport",
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageToNull: state => {
            state.page = initialState.page;
        },
        setFormState: (state, action) => {
            state.formState = action.payload;
        },
        clearFormState: state => {
            state.formState = initialState.formState;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setValidators: (state, action) => {
            state.validatorsList = action.payload;
        },
        setIsLoadingValidatorsList: (state, action) => {
            state.isLoadingValidatorsList = action.payload;
        },
        setIsLoadingValidationReportExport: (state, action) => {
            state.isExportValidationReportLoading = action.payload;
        },
        setIsDialogOpenValidationReportExport: (state, action) => {
            state.isDialogOpenValidationReportExport = action.payload;
        },
    },
});

export const {
    setIsLoading,
    setPage,
    setPageToNull,
    setFormState,
    clearFormState,
    setSortColumn,
    setIsLoadingValidatorsList,
    setValidators,
    setIsLoadingValidationReportExport,
    setIsDialogOpenValidationReportExport,
} = validationReportSlice.actions;

export const selectIsLoading = state => {
    return state.validationReport.isLoading;
};
export const selectPage = state => {
    return state.validationReport.page;
};
export const selectFormState = state => {
    return state.validationReport.formState;
};
export const selectSortColumn = state => {
    return state.validationReport.sortColumn;
};
export const selectValidatorsList = state => {
    return state.validationReport.validatorsList;
};
export const selectIsLoadingValidatorsList = state => {
    return state.validationReport.isLoadingValidatorsList;
};

export const selectIsExportValidationReportLoading = state => {
    return state.validationReport.isExportValidationReportLoading;
};
export const selectIsDialogOpenValidationReportExport = state => {
    return state.validationReport.isDialogOpenValidationReportExport;
};

export const postValidationCountReportExportAPI = title => async (dispatch, getState) => {
    const { isExportValidationReportLoading, formState, sortColumn } = getState().validationReport;
    const { fromDate, toDate, validator, viewDetail } = formState;

    if (isExportValidationReportLoading) {
        return;
    }

    dispatch(setIsLoadingValidationReportExport(true));
    const response = await axios
        .post(
            buildApiV3Url(`export-validation-count-report`),
            {
                title,
                fromDate,
                toDate,
                validatorName: validator || "",
                showAccumulatedData: !viewDetail,
                orderBy: sortColumn[0],
            },
            { withCredentials: true }
        )
        .catch(() => {
            showToast(
                "error",
                "Failed to run the validation count report. Please try again later."
            );
        });
    dispatch(setIsDialogOpenValidationReportExport(false));
    dispatch(setIsLoadingValidationReportExport(false));

    if (response?.status === 200) {
        const baseURL = `https://${docLockerClient}`;
        window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
        showToast(
            "success",
            "We are working on your export. When it's ready, find it in",
            "export-success",
            true,
            "your downloads",
            () => {
                hideToast("export-success");
                window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
            },
            "The time it takes to export data depends on the size of the file and network traffic."
        );
        return;
    }
};

export const fetchValidationReports =
    (paginateDetails, pageNumber = 1, pageSize = rowsPerPageDefault) =>
    async (dispatch, getState) => {
        const { session, validationReport } = getState();
        const { isLoading, formState, sortColumn } = validationReport;
        const { rowsPerPage } = session;
        pageSize = pageSize || rowsPerPage;
        const { fromDate, toDate, validator, viewDetail } = formState;

        let requestBody = {
            from: fromDate,
            to: toDate,
            validatorName: validator || "",
            showAccumulatedData: !viewDetail,
            orderBy: sortColumn[0],
            orderByDirection: `${sortColumn[1] ? "ASC" : "DESC"}`,
            pageNumber,
            pageSize,
        };

        if (isLoading) {
            return;
        }

        dispatch(setIsLoading(true));
        const apiResponse = await axios
            .post(`${buildApiV3Url(`validation-report`)}`, requestBody, {
                withCredentials: true,
            })
            .catch(() => {
                showToast(
                    "error",
                    "Failed to fetch the validation report. Please try again later."
                );
            });

        if (!parseSuccessResponse(CRUDOperation.ReadAll, apiResponse)?.recordFound) {
            dispatch(setPageToNull());
        } else {
            const readAllResponse = parseGetAllObjResponse(apiResponse);

            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails?.pageNumber,
                      startIndex: paginateDetails?.startIndex,
                      endIndex: paginateDetails?.endIndex,
                      rowsPerPage: paginateDetails?.rowsPerPage,
                      totalRecords: readAllResponse?.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 20,
                      totalRecords: readAllResponse?.count,
                  };
            dispatch(
                setPage({
                    data: readAllResponse.value,
                    paginate: paginateData,
                })
            );
        }

        dispatch(setIsLoading(false));
    };

export const fetchValidators = () => async (dispatch, getState) => {
    const { validationReport } = getState();
    const { isLoadingValidatorList } = validationReport;

    if (isLoadingValidatorList) {
        return;
    }

    dispatch(setIsLoadingValidatorsList(true));
    const apiResponse = await axios
        .get(`${buildApiV3Url(`validation-report/validators`)}`, {
            withCredentials: true,
        })
        .catch(() => {
            showToast("error", "Failed to fetch the validators. Please try again later.");
        });

    if (parseSuccessResponse(CRUDOperation.ReadAll, apiResponse)?.recordFound) {
        dispatch(setValidators(parseGetAllObjResponse(apiResponse).value));
    }

    dispatch(setIsLoadingValidatorsList(false));
};
export default validationReportSlice.reducer;
